import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Result, Space, Spin, Text, useToast } from "@app/ui";
import { api } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import { AuthShell } from "../auth/AuthShell.js";
import { useT } from "../i18n.js";
import { useErrorText } from "../errors/index.js";

/**
 * Email-verification landing page. Consumes the token from the link, confirms
 * the address, and — if the user is signed in — refreshes their profile so the
 * "verify your email" banner clears. Public route (the token is the credential).
 * On failure (expired/used token) a signed-in user can request a fresh email.
 */
export function VerifyEmailPage() {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const errorText = useErrorText();
  const { user, refreshMe } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [message, setMessage] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard React 18 strict-mode double-invoke (token is single-use)
    ran.current = true;
    if (!token) {
      setState("error");
      setMessage(t("verify.missingToken"));
      return;
    }
    void (async () => {
      try {
        await api<void>("/auth/verify-email", { method: "POST", body: { token }, noRetry: true });
        if (user) await refreshMe().catch(() => undefined);
        setState("ok");
      } catch (e) {
        setState("error");
        setMessage(errorText(e));
      }
    })();
  }, [token]);

  const resend = async () => {
    setResending(true);
    try {
      await api<void>("/auth/verify-email/resend", { method: "POST", noRetry: true });
      toast.success(t("verify.resent"));
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setResending(false);
    }
  };

  if (state === "loading") {
    return (
      <AuthShell>
        <div className="auth-wait">
          <Spin />
          <Text secondary>{t("verify.verifying")}</Text>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Result
        status={state === "ok" ? "success" : "error"}
        title={state === "ok" ? t("verify.successTitle") : t("verify.errorTitle")}
        subTitle={state === "ok" ? t("verify.successBody") : message}
        extra={
          <Space>
            {state === "error" && user && (
              <Button loading={resending} onClick={() => void resend()}>
                {t("verify.resend")}
              </Button>
            )}
            <Button variant="primary" onClick={() => navigate(user ? "/" : "/login")}>
              {user ? t("verify.goDashboard") : t("verify.goLogin")}
            </Button>
          </Space>
        }
      />
    </AuthShell>
  );
}
